import { FC, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import {
    Avatar,
    Menu,
    MenuDivider,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Persona,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../libs/hooks/useChat';
import { useRequestNewChat } from '../../libs/hooks/useRequestNewChat';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState, resetState } from '../../redux/app/store';
import { HostContext } from '../../embed-script/HostContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ResponseStyleSwitcher } from './ResponseStyleSwitcher';
import { AuthHelper, AuthType } from '../../libs/auth/AuthHelper';
import { AppState } from '../../AppState';

export const useClasses = makeStyles({
    root: {
        marginRight: tokens.spacingHorizontalXL,
    },
    persona: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingVerticalMNudge}`,
        overflowWrap: 'break-word',
    },
});

interface IUserSettingsProps {
    setLoadingState: () => void;
    hostContext?: HostContext;
    setAppState: React.Dispatch<React.SetStateAction<AppState>>;
}

export const UserSettingsMenu: FC<IUserSettingsProps> = ({ setLoadingState, hostContext, setAppState }) => {
    const classes = useClasses();
    const chat = useChat();
    const requestNewChat = useRequestNewChat();
    const { activeUserInfo, serviceInfo } = useAppSelector((state: RootState) => state.app);
    const responseStylePreferenceEnabled = serviceInfo.featureFlags.ResponseStylePreferenceEnabled;
    const auth = useAuth();

    const onLogout = useCallback(() => {
        setLoadingState();
        void auth.signoutRedirect();
        localStorage.setItem('oidcAutoLastLogin', new Date().toISOString());
        resetState();
    }, [auth, setLoadingState]);

    const { t } = useTranslation();

    const onNewChat = useCallback(() => {
        requestNewChat(() => {
            setLoadingState();
            return chat
                .createChat(hostContext)
                .then(() => {
                    if (AuthHelper.getAuthType() === AuthType.None) {
                        resetState();
                        setAppState(AppState.SettingUserInfo);
                    }
                })
                .catch(() => {});
        });
    }, [requestNewChat, chat, setLoadingState, hostContext, setAppState]);

    return (
        <>
            {
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        {
                            <Avatar
                                className={classes.root}
                                key={activeUserInfo?.username}
                                name={activeUserInfo?.username}
                                color={'brand'}
                                size={28}
                                badge={undefined}
                                data-testid="userSettingsButton"
                            />
                        }
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            <Persona
                                className={classes.persona}
                                name={activeUserInfo?.username}
                                secondaryText={activeUserInfo?.email}
                                presence={undefined}
                                avatar={{ color: 'brand' }}
                            />
                            <MenuDivider />
                            <MenuItem data-testid="createChatMenuButton" onClick={onNewChat}>
                                {t('userSettingsMenu.newChat')}
                            </MenuItem>
                            <LanguageSwitcher />
                            {responseStylePreferenceEnabled && <ResponseStyleSwitcher />}
                            <MenuItem data-testid="logOutMenuButton" onClick={onLogout}>
                                {t('userSettingsMenu.logOut')}
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            }
        </>
    );
};
